import { HttpErrorResponse } from '@angular/common/http';

/** 연결 자체가 실패한 경우 — 서버가 보낸 문구가 있을 리 없다 */
const NETWORK_MESSAGE = '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.';

/**
 * 게이트웨이가 낸 응답 — 서버가 아니라 그 앞의 프록시가 답한 것이라 본문이 HTML이고 `message`가
 * 없다. 서버가 같은 코드로 JSON `message`를 보냈다면 그쪽이 우선이다(호출부가 먼저 본다).
 *
 *   502 Bad Gateway — 서버가 내려가 있거나 재시작 중. 시간 문제가 아니라 기간을 좁히라고 하지 않는다
 *   504 Gateway Timeout · 524 Cloudflare 원 서버 타임아웃 — 응답이 길어 끊겼다. 긴 조회에서 나온다
 */
const BAD_GATEWAY_MESSAGE = '서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.';
const GATEWAY_TIMEOUT_MESSAGE =
  '응답 시간이 길어 연결이 끊겼습니다. 잠시 후 다시 시도하거나, 조회라면 기간을 좁혀 주세요.';

/** 게이트웨이 상태 코드의 전용 문구 — 해당 없으면 null */
function gatewayMessage(status: number): string | null {
  if (status === 502) {
    return BAD_GATEWAY_MESSAGE;
  }
  if (status === 504 || status === 524) {
    return GATEWAY_TIMEOUT_MESSAGE;
  }
  return null;
}

/** 응답 본문 객체에서 표시용 message를 꺼낸다 (형태가 다르면 null) */
function messageOf(body: unknown): string | null {
  if (
    body !== null &&
    typeof body === 'object' &&
    'message' in body &&
    typeof body.message === 'string'
  ) {
    return body.message;
  }
  return null;
}

/**
 * 서버 에러 응답 {code, message}에서 사용자 표시용 message를 추출한다.
 * 승인대기/잠금(403)은 code가 동일(FORBIDDEN)하고 message로 구분되므로 message를 그대로 표시한다.
 *
 * ⚠ **다운로드(responseType: 'blob') 요청에는 쓰지 마라** — 그때는 오류 본문도 `Blob`으로
 * 와서 여기서 사유를 꺼낼 수 없다. `extractDownloadErrorMessage`가 그 자리다.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return NETWORK_MESSAGE;
    }
    const message = messageOf(error.error);
    if (message !== null) {
      return message;
    }
    return gatewayMessage(error.status) ?? fallback;
  }
  return fallback;
}

/**
 * 다운로드 요청(`responseType: 'blob'`)의 오류 문구.
 *
 * 왜 따로 있나: 그 요청은 **성공 응답을 Blob으로 읽겠다고 선언**한 것이라 Angular가 **오류 본문도
 * Blob으로** 준다. `error.error`가 `{ code, message }` 객체가 아니라 그 JSON을 담은 Blob이므로
 * `extractErrorMessage`는 사유를 못 꺼내고 폴백만 낸다 — 서버가 "조회 건수가 많습니다" 같은
 * 이유를 보내도 화면에는 "엑셀 다운로드 중 오류가 발생했습니다"만 뜬다.
 *
 * Blob은 비동기로만 읽히므로 Promise를 돌려준다. JSON이 아니면(프록시·게이트웨이가 낸 HTML 등)
 * 폴백으로 떨어진다 — 화면에 남의 오류 페이지 원문을 그대로 붙이지 않는다.
 */
export async function extractDownloadErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
    if (error.status === 0) {
      return NETWORK_MESSAGE;
    }
    let message: string | null = null;
    try {
      message = messageOf(JSON.parse(await error.error.text()));
    } catch {
      message = null;
    }
    if (message !== null) {
      return message;
    }
    return gatewayMessage(error.status) ?? fallback;
  }
  return extractErrorMessage(error, fallback);
}
